import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import { useStore } from '../store/useStore';
import { toNano, beginCell, Address } from '@ton/core';
import { Buffer } from 'buffer';
import './DepositModalSimple.css';

// Полифилл для Buffer в браузере
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = Buffer;
}

// Тип выбора валюты депозита
// SPERM_BUY - купить SPERM за TON (конвертация)
// SPERM_DIRECT - внести SPERM напрямую (прямой депозит)
// USDT - внести USDT
type DepositCurrency = 'SPERM_BUY' | 'SPERM_DIRECT' | 'USDT';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  telegramId: string;
}

const DepositModalSimple: React.FC<DepositModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  telegramId
}) => {
  const { t } = useTranslation();
  const [tonConnectUI] = useTonConnectUI();
  const walletAddress = useTonAddress();
  const { user } = useStore(); // Получаем игровой баланс
  const [depositCurrency, setDepositCurrency] = useState<DepositCurrency | null>(null); // Выбор валюты
  const [spermAmount, setSpermAmount] = useState('50'); // Сумма SPERM
  const [usdtAmount, setUsdtAmount] = useState('50'); // Сумма USDT
  const [loading, setLoading] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(85); // По умолчанию 85 SPERM за TON
  // Бэк не зачисляет меньше MIN_DEPOSIT_TON — переводим порог в SPERM по курсу
  const MIN_DEPOSIT_TON = 0.02;
  const minSperm = exchangeRate > 0 ? Math.ceil(MIN_DEPOSIT_TON * exchangeRate) : 1;
  const [error, setError] = useState('');
  const [status, setStatus] = useState(''); // Для отображения статуса
  const [showOpenWallet, setShowOpenWallet] = useState(false); // Показывать ли кнопку Open Wallet

  // Системный кошелек для приема депозитов
  const SYSTEM_WALLET = 'UQAvK4BqL9ySHgaRaQ25Vd5c7LcgpX7ypoNEnATO3RtDRIs8';

  // USDT Master контракт в TON mainnet
  const USDT_MASTER = 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';

  // SPERM Master контракт
  const SPERM_MASTER = 'EQC4yNXijh2EJqDpPunsjjNeLG7VQFdbVH64QIX5aowyX0bh';

  // State для прямого депозита SPERM
  const [spermDirectAmount, setSpermDirectAmount] = useState('100'); // Сумма SPERM для прямого депозита
  const [userSpermBalance, setUserSpermBalance] = useState<number | null>(null); // Баланс SPERM в кошельке
  const [marathonAgreed, setMarathonAgreed] = useState(false); // Checkbox для согласия с правилами марафона

  // Получаем актуальный курс
  useEffect(() => {
    fetchRate();
    const interval = setInterval(fetchRate, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Отслеживаем изменения подключения кошелька
  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange((wallet) => {
      console.log('Статус кошелька изменился:', wallet);
    });
    
    return () => {
      unsubscribe();
    };
  }, [tonConnectUI]);
  
  const fetchRate = async () => {
    try {
      const response = await fetch('/api/exchange/rate');
      const data = await response.json();
      if (data.success && data.rate?.tonToSperm) {
        setExchangeRate(data.rate.tonToSperm);
      }
    } catch (error) {
      console.error('Ошибка получения курса:', error);
    }
  };

  const handleDeposit = async () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    console.log('=== НАЧАЛО handleDeposit ===');
    console.log(`Платформа iOS? ${isIOS}`);
    console.log('telegramId:', telegramId);

    // КРИТИЧЕСКАЯ ПРОВЕРКА: telegramId должен быть заполнен!
    if (!telegramId || telegramId.trim() === '') {
      console.error('ОШИБКА: telegramId пустой!');
      setError(t('modals.simpleDepositModal.errorNoTelegramId') || 'Ошибка идентификации пользователя. Перезагрузите приложение.');
      return;
    }

    setStatus(t('depositModal.statusPreparing'));
    setShowOpenWallet(false);

    // Рассчитываем сколько TON нужно для получения нужного количества SPERM
    const desiredSperm = parseFloat(spermAmount);
    const tonAmount = exchangeRate > 0 ? desiredSperm / exchangeRate : 0;

    // Валидация - минимум 0.02 TON в пересчёте на SPERM
    if (!desiredSperm || desiredSperm < minSperm) {
      setError(t('depositModal.errorMinSpermAmount', { min: minSperm }));
      setStatus('');
      return;
    }

    if (!walletAddress) {
      // Если кошелек не подключен, подключаем
      try {
        console.log('Кошелек не подключен, пытаемся подключить...');
        setStatus(t('modals.simpleDepositModal.connectWallet'));
        await tonConnectUI.connectWallet();
        setStatus('');
        return; // После подключения пользователь должен нажать еще раз
      } catch (err) {
        console.error('Ошибка подключения кошелька:', err);
        setError(t('depositModal.errorConnectWallet'));
        setStatus('');
        return;
      }
    }
    
    setLoading(true);
    setError('');

    try {
      // Создаем payload с комментарием для идентификации пользователя
      const comment = `deposit_${telegramId}`;
      console.log('Creating comment for transaction:', comment);
      
      // Формируем payload с помощью beginCell
      const body = beginCell()
        .storeUint(0, 32)           // op code для текстового комментария
        .storeStringTail(comment)    // сам комментарий с ID пользователя
        .endCell();
      
      const payload = body.toBoc().toString('base64');
      console.log('Generated payload:', payload);
      
      // Структура транзакции
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут (увеличено для v4r2 кошельков)
        messages: [
          {
            address: SYSTEM_WALLET,
            amount: toNano(tonAmount.toFixed(4)).toString(), // округляем до 4 знаков (как в StakingPage)
            payload: payload // добавляем payload с комментарием
          }
        ]
      };
      
      // Отправляем транзакцию
      const sendPromise = tonConnectUI.sendTransaction(transaction);

      // Показываем статус и кнопку Open Wallet для iOS
      setStatus(t('modals.simpleDepositModal.confirmTransaction'));
      
      // Для iOS показываем кнопку Open Wallet
      if (isIOS) {
        setShowOpenWallet(true);
      }
      
      // НЕ закрываем модалку, ждем результат
      sendPromise.then(async (result: any) => {
        console.log('Транзакция успешно отправлена:', result);

        if (result && result.boc) {
          setStatus(t('modals.simpleDepositModal.transactionSent'));
          setShowOpenWallet(false);

          // Регистрируем депозит на backend
          try {
            const response = await fetch('/api/deposits/create', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({
                amount: tonAmount,
                currency: 'TON',
                tx_hash: result.boc,
                telegram_id: telegramId
              })
            });

            if (response.ok) {
              // Показываем ожидаемую сумму (реальная будет начислена после подтверждения свопа)
              setStatus(t('modals.simpleDepositModal.successPending', { amount: desiredSperm.toFixed(0) }));
              setLoading(false);
              
              // Закрываем модалку через 3 секунды после успеха
              setTimeout(() => {
                onClose();
                if (onSuccess) {
                  onSuccess();
                }
              }, 3000);
            } else {
              setStatus(t('depositModal.statusFailed'));
              setError(t('depositModal.errorContact'));
              setLoading(false);
            }
          } catch (err) {
            console.error('Ошибка регистрации депозита:', err);
            setStatus(t('depositModal.errorProcessing'));
            setLoading(false);
          }
        }
      }).catch((error: any) => {
        console.error('Ошибка транзакции:', error);
        console.log('Детали ошибки:', JSON.stringify(error, null, 2));
        setShowOpenWallet(false);
        setLoading(false);
        setStatus('');

        const errorMsg = error?.message?.toLowerCase() || '';

        if (errorMsg.includes('user rejected') || errorMsg.includes('cancel') || errorMsg.includes('denied')) {
          setError(t('modals.simpleDepositModal.transactionCanceled'));
        } else if (errorMsg.includes('insufficient') || errorMsg.includes('balance')) {
          setError(t('modals.simpleDepositModal.insufficientTonForGas'));
        } else if (errorMsg.includes('timeout') || errorMsg.includes('expired')) {
          setError(t('depositModal.statusTimeout'));
        } else if (errorMsg.includes('network') || errorMsg.includes('connection')) {
          setError(t('depositModal.errorSendTransaction'));
        } else {
          setError(t('modals.simpleDepositModal.error', { message: error.message || 'Unknown error' }));
        }
      });

    } catch (error: any) {
      console.error('Ошибка при подготовке транзакции:', error);
      setError(t('modals.simpleDepositModal.error', { message: error?.message || 'Unknown error' }));
      setLoading(false);
      setShowOpenWallet(false);
      setStatus(''); // Очищаем статус "Preparing"
    }
  };
  
  // Быстрые суммы в SPERM (компактно - только 50 и 100)
  const quickAmounts = [50, 100];

  // Расчет необходимых TON для получения SPERM
  // Добавляем 0.01 TON на комиссию сети
  const NETWORK_FEE_TON = 0.01;
  const desiredSpermValue = parseFloat(spermAmount || '0');
  const rawTon = (desiredSpermValue / exchangeRate) + NETWORK_FEE_TON;
  // Убираем лишние нули: 2.5 вместо 2.5000
  const estimatedTon = rawTon % 1 === 0 ? rawTon.toFixed(0) : parseFloat(rawTon.toFixed(4)).toString();
  
  // Обработчик депозита USDT
  const handleUsdtDeposit = async () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    console.log('=== НАЧАЛО handleUsdtDeposit ===');
    console.log('telegramId:', telegramId);

    // КРИТИЧЕСКАЯ ПРОВЕРКА: telegramId должен быть заполнен!
    if (!telegramId || telegramId.trim() === '') {
      console.error('ОШИБКА: telegramId пустой!');
      setError(t('modals.simpleDepositModal.errorNoTelegramId') || 'Ошибка идентификации пользователя. Перезагрузите приложение.');
      return;
    }

    setStatus(t('depositModal.statusPreparing'));
    setShowOpenWallet(false);

    const desiredUsdt = parseFloat(usdtAmount);

    // Валидация - минимум 1 USDT
    if (!desiredUsdt || desiredUsdt < 1) {
      setError(t('modals.simpleDepositModal.minAmountUsdt'));
      setStatus('');
      return;
    }

    if (!walletAddress) {
      try {
        console.log('Кошелек не подключен, пытаемся подключить...');
        setStatus(t('modals.simpleDepositModal.connectWallet'));
        await tonConnectUI.connectWallet();
        setStatus('');
        return;
      } catch (err) {
        console.error('Ошибка подключения кошелька:', err);
        setError(t('depositModal.errorConnectWallet'));
        setStatus('');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Получаем jetton wallet адрес пользователя через TON API
      console.log('Запрашиваем jetton wallet для:', walletAddress);
      const jettonWalletResponse = await fetch(
        `https://tonapi.io/v2/accounts/${walletAddress}/jettons/${USDT_MASTER}`
      );

      if (!jettonWalletResponse.ok) {
        console.error('TON API ответ:', jettonWalletResponse.status);
        setError(t('modals.simpleDepositModal.usdtWalletError'));
        setLoading(false);
        setStatus('');
        return;
      }

      const jettonWalletData = await jettonWalletResponse.json();
      console.log('Данные jetton wallet:', jettonWalletData);

      // Проверяем баланс (USDT имеет 6 decimals)
      const usdtBalance = parseFloat(jettonWalletData.balance || '0') / 1000000;
      console.log('Баланс USDT:', usdtBalance, 'Требуется:', desiredUsdt);

      // Добавляем допуск 0.01 USDT для погрешности округления (49.999999 ≈ 50)
      if (usdtBalance + 0.01 < desiredUsdt) {
        setError(t('modals.simpleDepositModal.insufficientUsdtBalance', { balance: usdtBalance.toFixed(2) }));
        setLoading(false);
        setStatus('');
        return;
      }

      // Получаем адрес jetton wallet в правильном формате
      const userJettonWalletRaw = jettonWalletData.wallet_address?.address;
      console.log('Raw jetton wallet address:', userJettonWalletRaw);

      if (!userJettonWalletRaw) {
        setError(t('modals.simpleDepositModal.usdtWalletError'));
        setLoading(false);
        setStatus('');
        return;
      }

      // Парсим адреса
      const destinationAddress = Address.parse(SYSTEM_WALLET);
      // Конвертируем raw адрес в user-friendly формат
      const userJettonWallet = Address.parseRaw(userJettonWalletRaw).toString();
      console.log('User jetton wallet (parsed):', userJettonWallet);

      // Формируем payload для jetton transfer согласно TEP-74
      // https://github.com/ton-blockchain/TEPs/blob/master/text/0074-jettons-standard.md
      const comment = `usdt_deposit_${telegramId}`;

      // Forward payload - текстовый комментарий
      const forwardPayload = beginCell()
        .storeUint(0, 32) // op code для текстового комментария
        .storeStringTail(comment)
        .endCell();

      // Сумма в микро-USDT (6 decimals)
      const usdtMicro = BigInt(Math.floor(desiredUsdt * 1000000));
      console.log('USDT микро-единиц:', usdtMicro.toString());

      // jetton_transfer#0f8a7ea5 query_id:uint64 amount:VarUInteger 16
      // destination:MsgAddressInt response_destination:MsgAddressInt
      // custom_payload:(Maybe ^Cell) forward_ton_amount:VarUInteger 16
      // forward_payload:(Either Cell ^Cell)
      const jettonTransferBody = beginCell()
        .storeUint(0x0f8a7ea5, 32) // op: jetton_transfer
        .storeUint(Date.now(), 64) // query_id - используем timestamp для уникальности
        .storeCoins(usdtMicro) // amount в микро-USDT (6 decimals)
        .storeAddress(destinationAddress) // destination - системный кошелек получит USDT
        .storeAddress(destinationAddress) // response_destination - куда вернуть излишки TON
        .storeBit(false) // custom_payload: null (Maybe ^Cell = 0)
        .storeCoins(toNano('0.01')) // forward_ton_amount: 0.01 TON для уведомления
        .storeBit(false) // forward_payload как inline (не ref) - пустой либо простой
        .storeBuilder(beginCell().storeUint(0, 32).storeStringTail(comment)) // inline forward payload
        .endCell();

      console.log('Jetton transfer body создан');

      // Формируем транзакцию
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут (увеличено для v4r2 кошельков)
        messages: [
          {
            address: userJettonWallet,
            amount: toNano('0.05').toString(), // 0.05 TON для газа (реально ~0.01)
            payload: jettonTransferBody.toBoc().toString('base64')
          }
        ]
      };

      console.log('Отправляем транзакцию:', transaction);
      setStatus(t('modals.simpleDepositModal.confirmUsdtTransfer'));

      if (isIOS) {
        setShowOpenWallet(true);
      }

      const result = await tonConnectUI.sendTransaction(transaction);
      console.log('USDT транзакция отправлена:', result);

      if (result && result.boc) {
        setStatus(t('modals.simpleDepositModal.usdtSent'));
        setShowOpenWallet(false);

        // Регистрируем депозит на backend
        try {
          const response = await fetch('/api/deposits/create-usdt', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              amount: desiredUsdt,
              currency: 'USDT',
              tx_hash: result.boc,
              telegram_id: telegramId
            })
          });

          if (response.ok) {
            setStatus(t('modals.simpleDepositModal.usdtPending', { amount: desiredUsdt }));
            setLoading(false);

            setTimeout(() => {
              onClose();
              if (onSuccess) {
                onSuccess();
              }
            }, 5000); // Даём время прочитать
          } else {
            setStatus(t('modals.simpleDepositModal.usdtPendingShort', { amount: desiredUsdt }));
            setLoading(false);
          }
        } catch (err) {
          console.error('Ошибка регистрации USDT депозита:', err);
          setStatus(t('modals.simpleDepositModal.usdtPendingShort', { amount: desiredUsdt }));
          setLoading(false);
        }
      }

    } catch (error: any) {
      console.error('Ошибка USDT транзакции:', error);
      setShowOpenWallet(false);
      setLoading(false);
      setStatus('');

      if (error.message?.includes('User rejected') || error.message?.includes('Canceled')) {
        setError(t('modals.simpleDepositModal.transactionCanceled'));
      } else if (error.message?.includes('Insufficient')) {
        setError(t('modals.simpleDepositModal.insufficientTonForGas'));
      } else {
        setError(t('modals.simpleDepositModal.error', { message: error.message || 'Unknown error' }));
      }
    }
  };

  // Получение баланса SPERM в кошельке пользователя
  const fetchSpermBalance = async () => {
    if (!walletAddress) {
      setUserSpermBalance(null);
      return;
    }

    try {
      const response = await fetch(
        `https://tonapi.io/v2/accounts/${walletAddress}/jettons/${SPERM_MASTER}`
      );

      if (response.ok) {
        const data = await response.json();
        console.log('TonAPI SPERM response:', data);
        // SPERM имеет 9 decimals - баланс в поле balance или jetton_wallet.balance
        const rawBalance = data.balance || data.jetton_wallet?.balance || '0';
        const balance = parseFloat(rawBalance) / 1000000000;
        console.log('Parsed SPERM balance:', balance);
        setUserSpermBalance(balance);
      } else {
        console.log('TonAPI SPERM error:', response.status);
        setUserSpermBalance(0);
      }
    } catch (error) {
      console.error('Ошибка получения баланса SPERM:', error);
      setUserSpermBalance(0);
    }
  };

  // Загружаем баланс SPERM при подключении кошелька или выборе прямого депозита
  useEffect(() => {
    if (walletAddress && depositCurrency === 'SPERM_DIRECT') {
      fetchSpermBalance();
      // Периодически обновляем баланс каждые 10 секунд
      const interval = setInterval(fetchSpermBalance, 10000);
      return () => clearInterval(interval);
    }
  }, [walletAddress, depositCurrency]);

  // Обработчик прямого депозита SPERM
  const handleSpermDirectDeposit = async () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    console.log('=== НАЧАЛО handleSpermDirectDeposit ===');
    console.log('telegramId:', telegramId);

    // КРИТИЧЕСКАЯ ПРОВЕРКА: telegramId должен быть заполнен!
    if (!telegramId || telegramId.trim() === '') {
      console.error('ОШИБКА: telegramId пустой!');
      setError(t('modals.simpleDepositModal.errorNoTelegramId') || 'Ошибка идентификации пользователя. Перезагрузите приложение.');
      return;
    }

    setStatus(t('depositModal.statusPreparing'));
    setShowOpenWallet(false);

    const desiredSperm = parseFloat(spermDirectAmount);

    // Валидация - минимум 1 SPERM
    if (!desiredSperm || desiredSperm < 1) {
      setError(t('modals.simpleDepositModal.minAmountSperm'));
      setStatus('');
      return;
    }

    if (!walletAddress) {
      try {
        console.log('Кошелек не подключен, пытаемся подключить...');
        setStatus(t('modals.simpleDepositModal.connectWallet'));
        await tonConnectUI.connectWallet();
        setStatus('');
        return;
      } catch (err) {
        console.error('Ошибка подключения кошелька:', err);
        setError(t('depositModal.errorConnectWallet'));
        setStatus('');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      // Получаем jetton wallet адрес пользователя через TON API
      console.log('Запрашиваем SPERM jetton wallet для:', walletAddress);
      const jettonWalletResponse = await fetch(
        `https://tonapi.io/v2/accounts/${walletAddress}/jettons/${SPERM_MASTER}`
      );

      if (!jettonWalletResponse.ok) {
        console.error('TON API ответ:', jettonWalletResponse.status);
        setError(t('modals.simpleDepositModal.spermWalletError'));
        setLoading(false);
        setStatus('');
        return;
      }

      const jettonWalletData = await jettonWalletResponse.json();
      console.log('Данные SPERM jetton wallet:', jettonWalletData);

      // Проверяем баланс (SPERM имеет 9 decimals)
      const spermBalance = parseFloat(jettonWalletData.balance || '0') / 1000000000;
      console.log('Баланс SPERM:', spermBalance, 'Требуется:', desiredSperm);

      // ВАЖНО: При отправке ВСЕГО баланса нужен запас ~2 монеты из-за округления в nano-единицах
      // Это связано с тем что floating point arithmetic может давать погрешность
      // Например: 1055.999999999 в API → 1055 при конвертации → не хватает 0.000000001
      const safetyBuffer = 2; // Запас 2 SPERM для безопасности округления

      if (spermBalance < desiredSperm) {
        // Если пытаются отправить больше чем есть - предлагаем уменьшить на 2
        const suggestedAmount = Math.floor(spermBalance) - safetyBuffer;
        if (suggestedAmount > 0 && desiredSperm > spermBalance - safetyBuffer) {
          setError(t('modals.simpleDepositModal.roundingError', {
            balance: Math.floor(spermBalance),
            suggested: suggestedAmount
          }));
        } else {
          setError(t('modals.simpleDepositModal.insufficientSpermBalance', { balance: spermBalance.toFixed(2) }));
        }
        setLoading(false);
        setStatus('');
        return;
      }

      // Если сумма равна или почти равна балансу - предупреждаем о возможной проблеме округления
      if (desiredSperm >= spermBalance - safetyBuffer) {
        console.warn('Сумма близка к полному балансу, возможна ошибка округления');
      }

      // Получаем адрес jetton wallet в правильном формате
      const userJettonWalletRaw = jettonWalletData.wallet_address?.address;
      console.log('Raw SPERM jetton wallet address:', userJettonWalletRaw);

      if (!userJettonWalletRaw) {
        setError(t('modals.simpleDepositModal.spermWalletError'));
        setLoading(false);
        setStatus('');
        return;
      }

      // Парсим адреса
      const destinationAddress = Address.parse(SYSTEM_WALLET);
      const userJettonWallet = Address.parseRaw(userJettonWalletRaw).toString();
      console.log('User SPERM jetton wallet (parsed):', userJettonWallet);

      // Формируем payload для jetton transfer согласно TEP-74
      // Комментарий для идентификации пользователя
      const comment = `sperm_deposit_${telegramId}`;

      // Сумма в nano-SPERM (9 decimals)
      const spermNano = BigInt(Math.floor(desiredSperm * 1000000000));
      console.log('SPERM nano-единиц:', spermNano.toString());

      // jetton_transfer#0f8a7ea5 query_id:uint64 amount:VarUInteger 16
      // destination:MsgAddressInt response_destination:MsgAddressInt
      // custom_payload:(Maybe ^Cell) forward_ton_amount:VarUInteger 16
      // forward_payload:(Either Cell ^Cell)
      const jettonTransferBody = beginCell()
        .storeUint(0x0f8a7ea5, 32) // op: jetton_transfer
        .storeUint(Date.now(), 64) // query_id - используем timestamp для уникальности
        .storeCoins(spermNano) // amount в nano-SPERM (9 decimals)
        .storeAddress(destinationAddress) // destination - системный кошелек получит SPERM
        .storeAddress(destinationAddress) // response_destination - куда вернуть излишки TON
        .storeBit(false) // custom_payload: null (Maybe ^Cell = 0)
        .storeCoins(toNano('0.01')) // forward_ton_amount: 0.01 TON для уведомления
        .storeBit(false) // forward_payload как inline (не ref)
        .storeBuilder(beginCell().storeUint(0, 32).storeStringTail(comment)) // inline forward payload
        .endCell();

      console.log('SPERM Jetton transfer body создан');

      // Формируем транзакцию
      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 минут
        messages: [
          {
            address: userJettonWallet,
            amount: toNano('0.05').toString(), // 0.05 TON для газа (реально ~0.01)
            payload: jettonTransferBody.toBoc().toString('base64')
          }
        ]
      };

      console.log('Отправляем SPERM транзакцию:', transaction);
      setStatus(t('modals.simpleDepositModal.confirmSpermTransfer'));

      if (isIOS) {
        setShowOpenWallet(true);
      }

      const result = await tonConnectUI.sendTransaction(transaction);
      console.log('SPERM транзакция отправлена:', result);

      if (result && result.boc) {
        setStatus(t('modals.simpleDepositModal.spermSent'));
        setShowOpenWallet(false);

        // Регистрируем депозит на backend
        try {
          const response = await fetch('/api/deposits/create-sperm', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              amount: desiredSperm,
              currency: 'SPERM',
              tx_hash: result.boc,
              telegram_id: telegramId
            })
          });

          if (response.ok) {
            setStatus(t('modals.simpleDepositModal.spermPending', { amount: desiredSperm }));
            setLoading(false);

            setTimeout(() => {
              onClose();
              if (onSuccess) {
                onSuccess();
              }
            }, 5000);
          } else {
            setStatus(t('modals.simpleDepositModal.spermPendingShort', { amount: desiredSperm }));
            setLoading(false);
          }
        } catch (err) {
          console.error('Ошибка регистрации SPERM депозита:', err);
          setStatus(t('modals.simpleDepositModal.spermPendingShort', { amount: desiredSperm }));
          setLoading(false);
        }
      }

    } catch (error: any) {
      console.error('Ошибка SPERM транзакции:', error);
      setShowOpenWallet(false);
      setLoading(false);
      setStatus('');

      const errorMsg = error.message?.toLowerCase() || '';

      if (errorMsg.includes('user rejected') || errorMsg.includes('canceled') || errorMsg.includes('cancel')) {
        setError(t('modals.simpleDepositModal.transactionCanceled'));
      } else if (errorMsg.includes('insufficient')) {
        setError(t('modals.simpleDepositModal.insufficientTonForGas'));
      } else if (errorMsg.includes('was not sent') || errorMsg.includes('not sent')) {
        // Ошибка TonConnectSDKError: Transaction was not sent
        // Скорее всего проблема с округлением - предлагаем уменьшить сумму
        setError(t('modals.simpleDepositModal.transactionNotSentRounding'));
      } else {
        setError(t('modals.simpleDepositModal.error', { message: error.message || 'Unknown error' }));
      }
    }
  };

  // Быстрые суммы для прямого депозита SPERM (3 кнопки + Max)
  const quickSpermDirectAmounts = [5, 50, 500];

  // Сброс выбора валюты при закрытии
  const handleClose = () => {
    setDepositCurrency(null);
    setError('');
    setStatus('');
    onClose();
  };

  if (!isOpen) return null;

  // Определяем, мобильное ли устройство
  const isMobile = window.innerWidth <= 480;

  // Быстрые суммы для USDT
  const quickUsdtAmounts = [50, 100, 500, 1000];

  return (
    <div
      className="deposit-simple-overlay"
      onClick={handleClose}
      style={isMobile ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      } : {}}
    >
      <div
        className="deposit-simple-modal"
        onClick={(e) => e.stopPropagation()}
        style={isMobile ? {
          maxWidth: '100%',
          borderRadius: '24px',
          margin: 'auto'
        } : {}}
      >
        {/* Заголовок */}
        <div className="deposit-simple-header">
          <h2>
            {!depositCurrency && t('modals.simpleDepositModal.title')}
            {depositCurrency === 'SPERM_BUY' && t('modals.simpleDepositModal.titleBuySperm')}
            {depositCurrency === 'SPERM_DIRECT' && t('modals.simpleDepositModal.titleDepositSperm')}
            {depositCurrency === 'USDT' && t('modals.simpleDepositModal.titleDepositUsdt')}
          </h2>
          <button className="close-btn" onClick={handleClose}>✕</button>
        </div>

        {/* Контент */}
        <div className="deposit-simple-body">

          {/* ШАГ 1: Выбор валюты (если не выбрана) - 3 кнопки */}
          {!depositCurrency && (
            <div className="currency-selection">
              <p style={{ textAlign: 'center', color: '#888', marginBottom: '16px' }}>
                {t('modals.simpleDepositModal.chooseMethod')}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {/* Кнопка 1: Купить SPERM за TON */}
                <button
                  onClick={() => setDepositCurrency('SPERM_BUY')}
                  style={{
                    padding: '14px 8px',
                    borderRadius: '14px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 3px 12px rgba(102, 126, 234, 0.3)'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>💎</span>
                  <span>{t('modals.simpleDepositModal.buySpermBtn')}</span>
                  <span>{t('modals.simpleDepositModal.buySpermLabel')}</span>
                  <span style={{ fontSize: '9px', opacity: 0.8 }}>{t('modals.simpleDepositModal.buySpermHint')}</span>
                </button>

                {/* Кнопка 2: Внести SPERM напрямую */}
                <button
                  onClick={() => setDepositCurrency('SPERM_DIRECT')}
                  style={{
                    padding: '14px 8px',
                    borderRadius: '14px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #f7931a 0%, #ffb347 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 3px 12px rgba(247, 147, 26, 0.3)'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>🪙</span>
                  <span>{t('modals.simpleDepositModal.depositSpermBtn')}</span>
                  <span>{t('modals.simpleDepositModal.depositSpermLabel')}</span>
                  <span style={{ fontSize: '9px', opacity: 0.8 }}>{t('modals.simpleDepositModal.depositSpermHint')}</span>
                </button>

                {/* Кнопка 3: Внести USDT */}
                <button
                  onClick={() => setDepositCurrency('USDT')}
                  style={{
                    padding: '14px 8px',
                    borderRadius: '14px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #26a17b 0%, #1a8f6a 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: '0 3px 12px rgba(38, 161, 123, 0.3)'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>💵</span>
                  <span>{t('modals.simpleDepositModal.depositUsdtBtn')}</span>
                  <span>{t('modals.simpleDepositModal.depositUsdtLabel')}</span>
                  <span style={{ fontSize: '9px', opacity: 0.8 }}>{t('modals.simpleDepositModal.depositUsdtHint')}</span>
                </button>
              </div>

              <p style={{
                textAlign: 'center',
                color: '#666',
                fontSize: '11px',
                marginTop: '12px',
                padding: '10px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                lineHeight: '1.4'
              }} dangerouslySetInnerHTML={{ __html:
                `${t('modals.simpleDepositModal.buySpermDesc')}<br/>${t('modals.simpleDepositModal.depositSpermDesc')}<br/>${t('modals.simpleDepositModal.depositUsdtDesc')}`
              }} />
            </div>
          )}

          {/* ШАГ 2A: Форма покупки SPERM за TON (конвертация) */}
          {depositCurrency === 'SPERM_BUY' && (
            <>
              {/* Кнопка назад */}
              <button
                onClick={() => setDepositCurrency(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  fontSize: '14px'
                }}
              >
                {t('modals.simpleDepositModal.backToChoice')}
              </button>

              {/* Информационный блок */}
              <div className="info-block">
                {/* Курс и статус кошелька */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#888' }}>1 TON = {exchangeRate.toFixed(2)} SPERM</span>
                  {walletAddress ? (
                    <span style={{ color: '#4caf50', fontSize: '12px' }}>{t('modals.simpleDepositModal.walletConnected')}</span>
                  ) : (
                    <span style={{ color: '#ff9800', fontSize: '12px' }}>{t('modals.simpleDepositModal.connectWallet')}</span>
                  )}
                </div>

                {/* Расчет необходимых TON */}
                {spermAmount && parseFloat(spermAmount) > 0 && (
                  <div style={{
                    textAlign: 'center',
                    color: '#667eea',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    padding: '8px 0'
                  }}>
                    💎 {t('modals.simpleDepositModal.youNeedToPay')}: ~{estimatedTon} TON
                  </div>
                )}

                {/* Минимальные требования */}
                <div style={{
                  fontSize: '11px',
                  color: '#777',
                  textAlign: 'center',
                  paddingTop: '8px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  🪙 {t('modals.simpleDepositModal.minimumSperm', { min: minSperm })} • ⏱ {t('modals.simpleDepositModal.creditTime')} • 💰 {t('modals.simpleDepositModal.networkFee')}
                </div>
              </div>

              {/* Предупреждение о волатильности */}
              <div className="volatility-warning">
                {t('modals.simpleDepositModal.volatilityWarning')}
              </div>

              {/* Ввод суммы в SPERM */}
              <div className="amount-input-section">
                <label>{t('modals.simpleDepositModal.spermAmountLabel')}</label>
                <input
                  type="number"
                  value={spermAmount}
                  onChange={(e) => setSpermAmount(e.target.value)}
                  placeholder="50"
                  min={minSperm}
                  step="1"
                  disabled={loading}
                />

                {/* Быстрый выбор в SPERM */}
                <div className="quick-amounts">
                  {quickAmounts.map(val => (
                    <button
                      key={val}
                      className={`quick-amount ${spermAmount === val.toString() ? 'active' : ''}`}
                      onClick={() => setSpermAmount(val.toString())}
                      disabled={loading}
                    >
                      {val} 🪙
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ШАГ 2B: Форма депозита USDT */}
          {depositCurrency === 'USDT' && (
            <>
              {/* Кнопка назад */}
              <button
                onClick={() => setDepositCurrency(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#26a17b',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  fontSize: '14px'
                }}
              >
                {t('modals.simpleDepositModal.backToChoice')}
              </button>

              {/* Информационный блок USDT */}
              <div className="info-block" style={{ borderColor: 'rgba(38, 161, 123, 0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#26a17b', fontWeight: 'bold' }}>💵 USDT (TRC-20 on TON)</span>
                  {walletAddress ? (
                    <span style={{ color: '#4caf50', fontSize: '12px' }}>{t('modals.simpleDepositModal.walletConnected')}</span>
                  ) : (
                    <span style={{ color: '#ff9800', fontSize: '12px' }}>{t('modals.simpleDepositModal.connectWallet')}</span>
                  )}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#888',
                  textAlign: 'center',
                  padding: '8px 0',
                  borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  {t('modals.simpleDepositModal.usdtWarning')}<br/>
                  <span style={{ color: '#666', fontSize: '11px' }}>{t('modals.simpleDepositModal.usdtWarningHint')}</span>
                </div>
              </div>

              {/* Ввод суммы в USDT */}
              <div className="amount-input-section">
                <label>{t('modals.simpleDepositModal.usdtAmountLabel')}</label>
                <input
                  type="number"
                  value={usdtAmount}
                  onChange={(e) => setUsdtAmount(e.target.value)}
                  placeholder="10"
                  min="1"
                  step="1"
                  disabled={loading}
                />

                {/* Быстрый выбор USDT */}
                <div className="quick-amounts">
                  {quickUsdtAmounts.map(val => (
                    <button
                      key={val}
                      className={`quick-amount ${usdtAmount === val.toString() ? 'active' : ''}`}
                      onClick={() => setUsdtAmount(val.toString())}
                      disabled={loading}
                      style={{
                        background: usdtAmount === val.toString()
                          ? 'linear-gradient(135deg, #26a17b 0%, #1a8f6a 100%)'
                          : undefined
                      }}
                    >
                      {val} 💵
                    </button>
                  ))}
                </div>
              </div>

              {/* Красное предупреждение о Лунном марафоне */}
              <div style={{
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.5)',
                borderRadius: '12px',
                padding: '12px',
                marginTop: '12px'
              }}>
                <div style={{ color: '#f44336', fontSize: '13px', marginBottom: '8px' }}>
                  {t('modals.simpleDepositModal.marathonDepositWarning')}
                </div>
                <span style={{ fontSize: '12px' }}>
                  📖{' '}
                  <a
                    href="https://gitbook.safepermoon.com/#marathon"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#2196f3', textDecoration: 'underline' }}
                  >
                    Подробнее о правилах
                  </a>
                </span>
              </div>

              {/* Checkbox согласия с правилами */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '12px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#ccc'
              }}>
                <input
                  type="checkbox"
                  checked={marathonAgreed}
                  onChange={(e) => setMarathonAgreed(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#26a17b'
                  }}
                />
                {t('modals.simpleDepositModal.marathonAgreeCheckbox')}
              </label>
            </>
          )}

          {/* ШАГ 2C: Форма прямого депозита SPERM */}
          {depositCurrency === 'SPERM_DIRECT' && (
            <>
              {/* Кнопка назад */}
              <button
                onClick={() => setDepositCurrency(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f7931a',
                  cursor: 'pointer',
                  marginBottom: '12px',
                  fontSize: '14px'
                }}
              >
                {t('modals.simpleDepositModal.backToChoice')}
              </button>

              {/* Информационный блок для прямого депозита SPERM */}
              <div className="info-block" style={{ borderColor: 'rgba(247, 147, 26, 0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#f7931a', fontWeight: 'bold' }}>{t('modals.simpleDepositModal.directSpermTitle')}</span>
                  {walletAddress ? (
                    <span style={{ color: '#4caf50', fontSize: '12px' }}>{t('modals.simpleDepositModal.walletConnected')}</span>
                  ) : (
                    <span style={{ color: '#ff9800', fontSize: '12px' }}>{t('modals.simpleDepositModal.connectWallet')}</span>
                  )}
                </div>

                {/* Игровой баланс */}
                <div style={{
                  textAlign: 'center',
                  padding: '8px 0',
                  borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <span style={{ color: '#888', fontSize: '12px' }}>🎮 Игровой баланс: </span>
                  <span style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '14px' }}>
                    {user?.balance?.toFixed(2) || '0.00'} SPERM
                  </span>
                </div>

                {/* Баланс SPERM в TON кошельке */}
                {walletAddress && (
                  <div style={{
                    textAlign: 'center',
                    padding: '8px 0',
                    borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    <span style={{ color: '#888', fontSize: '12px' }}>💎 В TON кошельке: </span>
                    <span style={{ color: '#f7931a', fontWeight: 'bold', fontSize: '14px' }}>
                      {userSpermBalance !== null ? `${(Number(userSpermBalance) || 0).toFixed(2)} SPERM` : t('modals.simpleDepositModal.loading')}
                    </span>
                  </div>
                )}

                <div style={{
                  fontSize: '11px',
                  color: '#777',
                  textAlign: 'center',
                  paddingTop: '8px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  {t('modals.simpleDepositModal.directSpermHints')}
                </div>
              </div>

              {/* Ввод суммы SPERM */}
              <div className="amount-input-section">
                <label>{t('modals.simpleDepositModal.spermDirectAmountLabel')}</label>
                <input
                  type="number"
                  value={spermDirectAmount}
                  onChange={(e) => setSpermDirectAmount(e.target.value)}
                  placeholder="100"
                  min="1"
                  step="1"
                  disabled={loading}
                />

                {/* Быстрый выбор SPERM */}
                <div className="quick-amounts">
                  {quickSpermDirectAmounts.map(val => (
                    <button
                      key={val}
                      className={`quick-amount ${spermDirectAmount === val.toString() ? 'active' : ''}`}
                      onClick={() => setSpermDirectAmount(val.toString())}
                      disabled={loading}
                      style={{
                        background: spermDirectAmount === val.toString()
                          ? 'linear-gradient(135deg, #f7931a 0%, #ffb347 100%)'
                          : undefined
                      }}
                    >
                      {val} 🪙
                    </button>
                  ))}
                  {/* Кнопка MAX - отнимает 2 монеты для безопасности округления */}
                  <button
                    className={`quick-amount`}
                    onClick={() => {
                      // Отнимаем 2 монеты для безопасности из-за округления в nano-единицах
                      const maxAmount = Math.floor(userSpermBalance || 0) - 2;
                      if (maxAmount > 0) {
                        setSpermDirectAmount(maxAmount.toString());
                      }
                    }}
                    disabled={loading || !userSpermBalance || userSpermBalance < 3}
                    style={{
                      background: userSpermBalance && userSpermBalance >= 3
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : '#444',
                      opacity: userSpermBalance && userSpermBalance >= 3 ? 1 : 0.5
                    }}
                  >
                    MAX
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Статус - показываем только если есть */}
          {status && (
            <div className="status-msg" style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: status.includes('✅') ? '#d4edda' : '#fff3cd',
              color: status.includes('✅') ? '#155724' : '#856404',
              marginTop: '12px',
              marginBottom: '12px',
              textAlign: 'center',
              fontSize: '13px'
            }}>
              {status}

              {/* Кнопка Open Wallet для iOS */}
              {showOpenWallet && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => {
                      // Используем прямую ссылку на Tonkeeper приложение
                      const tonkeeperDeeplink = 'https://app.tonkeeper.com/';

                      if (window.Telegram?.WebApp) {
                        // В Telegram WebApp открываем через openLink
                        (window.Telegram.WebApp as any).openLink(tonkeeperDeeplink);
                      } else {
                        // В браузере открываем в новой вкладке
                        window.open(tonkeeperDeeplink, '_blank');
                      }
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    {t('modals.simpleDepositModal.openTonkeeper')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Ошибка */}
          {error && !status && (
            <div className="error-msg">
              {error}
            </div>
          )}
        </div>

        {/* Кнопки - показываем только если выбрана валюта */}
        {depositCurrency && (
          <div className="deposit-simple-footer">
            <button
              className="btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              {t('modals.simpleDepositModal.cancel')}
            </button>

            {/* Кнопка для покупки SPERM за TON */}
            {depositCurrency === 'SPERM_BUY' && (
              <button
                className="btn-deposit"
                onClick={handleDeposit}
                disabled={loading || !spermAmount || parseFloat(spermAmount) < minSperm}
              >
                {loading ? (
                  t('depositModal.processing')
                ) : !walletAddress ? (
                  t('modals.simpleDepositModal.connectAndDeposit')
                ) : (
                  t('modals.simpleDepositModal.depositSperm', { sperm: spermAmount || '0', ton: estimatedTon })
                )}
              </button>
            )}

            {/* Кнопка для прямого депозита SPERM */}
            {depositCurrency === 'SPERM_DIRECT' && (
              <button
                className="btn-deposit"
                onClick={handleSpermDirectDeposit}
                disabled={loading || !spermDirectAmount || parseFloat(spermDirectAmount) < 1}
                style={{
                  background: 'linear-gradient(135deg, #f7931a 0%, #ffb347 100%)'
                }}
              >
                {loading ? (
                  t('modals.simpleDepositModal.processingDirect')
                ) : !walletAddress ? (
                  t('modals.simpleDepositModal.connectAndDepositDirect')
                ) : (
                  t('modals.simpleDepositModal.depositSpermAmount', { amount: spermDirectAmount || '0' })
                )}
              </button>
            )}

            {/* Кнопка для депозита USDT */}
            {depositCurrency === 'USDT' && (
              <button
                className="btn-deposit"
                onClick={handleUsdtDeposit}
                disabled={loading || !usdtAmount || parseFloat(usdtAmount) < 1 || !marathonAgreed}
                style={{
                  background: (!marathonAgreed || loading || !usdtAmount || parseFloat(usdtAmount) < 1)
                    ? '#555'
                    : 'linear-gradient(135deg, #26a17b 0%, #1a8f6a 100%)',
                  opacity: (!marathonAgreed || loading || !usdtAmount || parseFloat(usdtAmount) < 1) ? 0.6 : 1
                }}
              >
                {loading ? (
                  t('modals.simpleDepositModal.processingUsdt')
                ) : !walletAddress ? (
                  t('modals.simpleDepositModal.connectAndDepositUsdt')
                ) : (
                  t('modals.simpleDepositModal.depositUsdtAmount', { amount: usdtAmount || '0' })
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositModalSimple;